import { CaretDownIcon } from "@phosphor-icons/react";
import dayjs from "dayjs";
import { useId, useMemo, useState } from "react";

import { type AdditionalServiceData } from "@/app/lib/context/FlightSelectionPayloadContext/FlightSelectionPayloadContext";
import { useSingleFlightSelectionContext } from "@/app/lib/context/SingleFlightDetailsContext";
import type { MappedServiceData } from "@/app/ui/flight-purchase/AdditionalServicesSelection/types";
import type {
  GetFlightAvailabilityTokenAirline,
  GetFlightAvailabilityTokenDestination,
} from "@/types/codegen";

import { ServiceSelect } from "./ServiceSelect";

type Props = {
  services: MappedServiceData[];
  selectedServices: AdditionalServiceData[];
  onServiceChange: (serviceData: AdditionalServiceData) => void;
};

export function ExpandablePanel({
  services,
  selectedServices,
  onServiceChange,
}: Props) {
  const id = useId();
  const [isExpanded, setIsExpanded] = useState(true);

  const { singleFlightDetails } = useSingleFlightSelectionContext();

  const { departureCode, arrivalCode } = services[0].service.route;

  const flightData = useMemo(() => {
    return singleFlightDetails.flights?.reduce<{
      airline: GetFlightAvailabilityTokenAirline;
      departure: GetFlightAvailabilityTokenDestination;
      arrival: GetFlightAvailabilityTokenDestination;
    } | null>((acc, flight) => {
      const departureSegment = flight.segments.find(
        (segment) => segment.departure.airport.code === departureCode,
      );

      const arrivalSegment = flight.segments.find(
        (segment) => segment.arrival.airport.code === arrivalCode,
      );

      if (departureSegment && arrivalSegment) {
        return {
          airline: departureSegment.operatingCarrier.airline,
          departure: departureSegment.departure,
          arrival: arrivalSegment.arrival,
        };
      }

      return acc;
    }, null);
  }, [arrivalCode, departureCode, singleFlightDetails.flights]);

  return (
    <div className="rounded-4xl bg-white p-3 shadow-sm">
      <h3 className="peer p-2">
        <button
          type="button"
          aria-expanded={isExpanded}
          aria-controls={id}
          className="group flex w-full items-center justify-between gap-2 text-start"
          onClick={() => setIsExpanded((prev) => !prev)}
        >
          <div>
            <div className="text-base font-medium">
              {flightData?.departure.city.name} -{" "}
              {flightData?.arrival.city.name}
            </div>
            <div className="text-sm text-text-tertiary">
              {dayjs(
                flightData?.departure.date + " " + flightData?.departure.time,
              ).format("ddd, DD MMM HH:MM")}{" "}
              -{" "}
              {dayjs(
                flightData?.arrival.date + " " + flightData?.arrival.time,
              ).format("ddd, DD MMM HH:MM")}
            </div>
            <div className="text-sm text-text-tertiary">
              {flightData?.airline.name}
            </div>
          </div>
          <CaretDownIcon
            size={20}
            className="shrink-0 transition-transform group-aria-expanded:rotate-180"
            weight="bold"
          />
        </button>
      </h3>
      <div
        id={id}
        className="flex max-h-0 w-full flex-col gap-2 overflow-hidden border-stroke-neutral-softer transition-all peer-has-aria-expanded:max-h-max peer-has-aria-expanded:border-t peer-has-aria-expanded:pt-4"
      >
        {services.map(({ service, mandatory }) => (
          <div
            key={service.bookingCode}
            className="rounded-xl bg-surface-neutral-soft p-4"
          >
            <ServiceSelect
              service={service}
              mandatory={mandatory}
              value={
                selectedServices.find(
                  (s) => s.service.bookingCode === service.bookingCode,
                )?.service.selectedOptionCode || ""
              }
              onChange={(optionId) => {
                const selectedService = {
                  mandatory,
                  service: {
                    bookingCode: service.bookingCode,
                    selectedOptionCode: optionId,
                  },
                };

                onServiceChange(selectedService);
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
